import { useQuery } from '@tanstack/react-query';
import { HelpCircle } from 'lucide-react';
import { ReactNode, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Coupon, CouponCurrency, CouponFormValues } from '@/@types/Coupons';
import CommissionInput from '@/components/shared/CommissionInput/CommissionInput';
import CustomSelect from '@/components/shared/CustomSelect/CustomSelect';
import { DropdownMultiSelect } from '@/components/shared/DropdownMultiSelect/DropdownMultiSelect';
import { Input } from '@/components/shared/Input/Input';
import { Label } from '@/components/shared/Label/Label';
import { Button } from '@/components/ui/button';
import { SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { formatCurrency } from '@/lib/select-options';
import { cn } from '@/lib/utils';
import { CouponsService } from '@/services/coupons.service';
import useModalStore from '@/store/useModalStore';
import { paymentMethods } from '@/utils/paymentMethods';
import {
  buildCouponPayload,
  COUPON_MIN_DISCOUNT_AMOUNT,
  couponToFormValues,
  emptyCouponFormValues,
  isCodeTakenMessage,
  normalizeCouponCode,
  toLocalDateInputValue,
} from './couponPayload';
import { CouponAmountField } from './CouponAmountField';
import { couponScopeCurrencies } from './couponScope';
import { useCouponMutations, useCouponOffers } from './hooks/useFetchCupons';

/** Campos que a API devolve em `errors` e o formulário sabe destacar. */
const API_FIELD_TO_FORM: Record<string, keyof CouponFormValues> = {
  name: 'name',
  discount_type: 'discountType',
  discount: 'discount',
  discount_amount: 'discountAmount',
  offer_ids: 'offerIds',
  payment_methods: 'paymentMethods',
  currency: 'currency',
  min_order_value: 'minOrderValue',
  max_uses: 'maxUses',
  max_uses_per_buyer: 'maxUsesPerBuyer',
  starts_at: 'startsAt',
  expires_at: 'expiresAt',
  status: 'status',
};

/** Ícone de ajuda para o que não é campo de texto. */
function InfoTooltip({ text }: { text: string }) {
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type='button'
            className='text-zinc-400 hover:text-zinc-300 transition-colors'
          >
            <HelpCircle className='w-4 h-4' />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side='right'
          className='max-w-xs bg-zinc-900 border-zinc-700 text-zinc-200 p-3'
        >
          <p className='text-sm whitespace-pre-line'>{text}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface SectionTitleProps {
  children: ReactNode;
  tooltip?: string;
}

function SectionTitle({ children, tooltip }: SectionTitleProps) {
  return (
    <div className='flex items-center gap-1.5 pt-2'>
      <h3 className='text-xs font-bold uppercase tracking-wider text-zinc-50'>
        {children}
      </h3>
      {tooltip && <InfoTooltip text={tooltip} />}
    </div>
  );
}

interface SwitchFieldProps {
  id: string;
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  className?: string;
  children: ReactNode;
}

/** Regra opcional: switch desligado esconde o campo e manda o valor neutro. */
function SwitchField({
  id,
  label,
  checked,
  onCheckedChange,
  className,
  children,
}: SwitchFieldProps) {
  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className='flex w-fit items-center gap-3 cursor-pointer'>
        <Switch
          id={id}
          checked={checked}
          onCheckedChange={onCheckedChange}
          data-cy={id}
        />
        <Label htmlFor={id}>{label}</Label>
      </div>
      {checked && <div className='sm:pl-14'>{children}</div>}
    </div>
  );
}

interface CouponFormProps {
  coupon?: Coupon | null;
}

export function CouponForm({ coupon = null }: CouponFormProps) {
  const { t } = useTranslation('common');
  const { closeModal } = useModalStore();
  const isEditing = Boolean(coupon);
  /** O POST da API recusa expiração no passado (`after_or_equal:today`). */
  const today = toLocalDateInputValue(new Date());
  /** Cupom já usado em venda tem desconto e tipo congelados pela API. */
  const isDiscountLocked = (coupon?.currentUses ?? 0) > 0;

  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    setError,
    formState: { errors },
  } = useForm<CouponFormValues>({
    defaultValues: coupon ? couponToFormValues(coupon) : emptyCouponFormValues,
  });

  const { submitCoupon, isSaving } = useCouponMutations({
    couponToEdit: coupon,
    onValidationError: (apiErrors) => {
      Object.entries(apiErrors).forEach(([field, messages]) => {
        const formField = API_FIELD_TO_FORM[field.split('.')[0]];

        if (formField) {
          setError(formField, {
            type: 'server',
            message: isCodeTakenMessage(field, messages[0])
              ? t('createCoupon.codeTaken')
              : messages[0],
          });
        }
      });
    },
  });

  const productOffers = useCouponOffers();

  useEffect(() => {
    reset(coupon ? couponToFormValues(coupon) : emptyCouponFormValues);
  }, [coupon, reset]);

  const discountType = watch('discountType');
  const status = watch('status');
  const startsAt = watch('startsAt');
  const selectedOfferIds = watch('offerIds');
  const selectedCurrency = watch('currency');
  const hasMinOrderValue = watch('hasMinOrderValue');
  const hasMaxUsesPerBuyer = watch('hasMaxUsesPerBuyer');
  const hasMaxUses = watch('hasMaxUses');
  const isPercentage = discountType === 'percentage';

  const offers = useMemo(
    () =>
      productOffers.map((offer) => ({
        id: String(offer.id),
        name: offer.name,
        price: offer.price,
        currency: offer.currency,
      })),
    [productOffers]
  );

  const offerOptions = useMemo(
    () =>
      offers.map((offer) => ({
        value: offer.id,
        label: offer.name,
        tag: offer.currency,
      })),
    [offers]
  );

  const scopeCurrencies = useMemo(
    () => couponScopeCurrencies(selectedOfferIds, offers),
    [selectedOfferIds, offers]
  );
  const isMultiCurrency = scopeCurrencies.length > 1;

  /*
    A moeda vem das ofertas marcadas: numa moeda só, é ela. Com BRL e USD, fica
    a escolhida nos cards. Cupom já usado mantém a moeda que a API congelou.
  */
  useEffect(() => {
    if (
      !isDiscountLocked &&
      scopeCurrencies.length > 0 &&
      !scopeCurrencies.includes(selectedCurrency)
    ) {
      setValue('currency', scopeCurrencies[0]);
    }
  }, [isDiscountLocked, scopeCurrencies, selectedCurrency, setValue]);

  const { data: usdToBrl, isLoading: isRateLoading } = useQuery({
    queryKey: ['currency-rate', 'USD', 'BRL'],
    queryFn: async () =>
      await CouponsService.getCurrencyRate({ from: 'USD', to: 'BRL' }),
    enabled: isMultiCurrency,
    staleTime: 5 * 60 * 1000,
  });

  /** Trocar a moeda-base apaga o que foi digitado na anterior. */
  const handleCurrencyChange = (currency: CouponCurrency) => {
    setValue('currency', currency);
    setValue('discountAmount', null);
    setValue('minOrderValue', 0);
  };

  const minDiscountLabel = formatCurrency(
    COUPON_MIN_DISCOUNT_AMOUNT / 100,
    selectedCurrency
  );

  const paymentMethodOptions = useMemo(
    () => paymentMethods.map((type) => ({ value: type, label: t(type) })),
    [t]
  );

  const discountTypeOptions = [
    { value: 'percentage', label: t('createCoupon.discountTypePercentage') },
    { value: 'fixed', label: t('createCoupon.discountTypeFixed') },
  ];

  const lockedDiscountLabel = isPercentage
    ? `${Number(coupon?.discount ?? 0)}%`
    : formatCurrency((coupon?.discountAmount ?? 0) / 100, selectedCurrency);

  const onSubmit = (values: CouponFormValues) => {
    submitCoupon(buildCouponPayload(values));
  };

  return (
    <section className='sm:overflow-y-hidden flex flex-col justify-between h-full'>
      <SheetHeader className='p-6'>
        <SheetTitle className='flex gap-2'>
          {isEditing ? t('createCoupon.editTitle') : t('createCoupon.createTitle')}
        </SheetTitle>
      </SheetHeader>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className='flex flex-col justify-between h-full overflow-hidden'
      >
        <section className='flex flex-col gap-5 px-6 pb-6 overflow-y-auto'>
          {isEditing && (
            <div className='flex w-fit items-center gap-3 cursor-pointer'>
              <Switch
                id='switch-coupon-status'
                checked={status === 'active'}
                onCheckedChange={(checked) =>
                  setValue('status', checked ? 'active' : 'inactive')
                }
                data-cy='switch-coupon-status'
              />
              <Label htmlFor='switch-coupon-status'>
                {t('createCoupon.activate')}
              </Label>
            </div>
          )}

          <Input
            label={t('createCoupon.code')}
            placeholder='CUPOM10'
            subLabel={t('createCoupon.codeHint')}
            data-cy='input-coupon-code'
            helperText={errors.name?.message}
            hasError={Boolean(errors.name)}
            {...register('name', {
              required: t('createCoupon.codeRequired'),
              minLength: { value: 3, message: t('createCoupon.codeMinLength') },
              maxLength: { value: 50, message: t('createCoupon.codeMaxLength') },
              pattern: {
                value: /^[A-Z0-9]+$/,
                message: t('createCoupon.codePattern'),
              },
              onChange: (event) => {
                setValue('name', normalizeCouponCode(event.target.value), {
                  shouldValidate: true,
                });
              },
            })}
          />

          {/*
            Antes do valor: são as moedas das ofertas marcadas que decidem os
            campos de desconto e de valor mínimo logo abaixo.
          */}
          <Controller
            control={control}
            name='offerIds'
            rules={{
              validate: (value) =>
                value !== null || t('createCoupon.offerRequired'),
            }}
            render={({ field }) => (
              <DropdownMultiSelect
                label={t('createCoupon.offer')}
                options={offerOptions}
                values={field.value}
                onValuesChange={field.onChange}
                allLabel={t('createCoupon.allOffers')}
                noneLabel={t('createCoupon.noOffers')}
                selectedCountLabel={(count) =>
                  t('createCoupon.selectedCount', { count })
                }
                helperText={errors.offerIds?.message}
                dataCy='select-coupon-offer'
              />
            )}
          />

          <Controller
            control={control}
            name='discountType'
            render={({ field }) => (
              <CustomSelect
                label={t('createCoupon.discountType')}
                placeholder={t('createCoupon.discountType')}
                options={discountTypeOptions}
                value={field.value}
                onValueChange={field.onChange}
                disabled={isDiscountLocked}
                dataCy='select-coupon-discount-type'
              />
            )}
          />

          {isDiscountLocked ? (
            <Input
              label={t('createCoupon.discount')}
              value={lockedDiscountLabel}
              subLabel={t('createCoupon.discountLocked')}
              data-cy='input-coupon-discount-locked'
              readOnly
              disabled
            />
          ) : isPercentage ? (
            <Controller
              control={control}
              name='discount'
              rules={{
                required: t('createCoupon.discountRequired'),
                validate: {
                  min: (value) => value >= 1 || t('createCoupon.discountMin'),
                  // 100% zera o pedido, e o checkout recusa preço final zero.
                  max: (value) =>
                    value < 100 || t('createCoupon.discountZeroesPrice'),
                },
              }}
              render={({ field }) => (
                <div className='flex flex-col gap-2'>
                  <CommissionInput
                    label={t('createCoupon.discount')}
                    placeholder='10'
                    dataCy='input-coupon-discount'
                    maxValue={100}
                    helperText={errors.discount?.message}
                    defaultValue={field.value ?? undefined}
                    onChangeCommission={(value) =>
                      field.onChange(value === '' ? null : Number(value))
                    }
                  />
                  <p className='text-sm text-zinc-500'>
                    {t('createCoupon.discountPercentageHint')}
                  </p>
                </div>
              )}
            />
          ) : (
            <Controller
              control={control}
              name='discountAmount'
              rules={{
                validate: (value) =>
                  (value ?? 0) >= COUPON_MIN_DISCOUNT_AMOUNT ||
                  t('createCoupon.discountAmountMin', {
                    value: minDiscountLabel,
                  }),
              }}
              render={({ field }) => (
                <CouponAmountField
                  label={t('createCoupon.discountAmount')}
                  value={field.value}
                  onChange={field.onChange}
                  currency={selectedCurrency}
                  scopeCurrencies={scopeCurrencies}
                  onCurrencyChange={handleCurrencyChange}
                  usdToBrl={usdToBrl}
                  isRateLoading={isRateLoading}
                  subLabel={t('createCoupon.discountAmountHint', {
                    value: minDiscountLabel,
                  })}
                  helperText={errors.discountAmount?.message}
                  dataCy='input-coupon-discount-amount'
                />
              )}
            />
          )}

          <Controller
            control={control}
            name='paymentMethods'
            rules={{
              validate: (value) =>
                value !== null || t('createCoupon.paymentMethodRequired'),
            }}
            render={({ field }) => (
              <DropdownMultiSelect
                label={t('createCoupon.paymentMethod')}
                options={paymentMethodOptions}
                values={field.value}
                onValuesChange={field.onChange}
                allLabel={t('createCoupon.allPaymentMethods')}
                noneLabel={t('createCoupon.noPaymentMethods')}
                selectedCountLabel={(count) =>
                  t('createCoupon.selectedCount', { count })
                }
                helperText={errors.paymentMethods?.message}
                dataCy='select-coupon-payment-method'
              />
            )}
          />

          <SectionTitle tooltip={t('createCoupon.rulesTooltip')}>
            {t('createCoupon.sectionRules')}
          </SectionTitle>

          <SwitchField
            id='switch-coupon-min-order-value'
            label={t('createCoupon.enableMinOrderValue')}
            checked={hasMinOrderValue}
            onCheckedChange={(checked) =>
              setValue('hasMinOrderValue', checked, { shouldValidate: true })
            }
          >
            <Controller
              control={control}
              name='minOrderValue'
              render={({ field }) => (
                <CouponAmountField
                  ariaLabel={t('createCoupon.minOrderValue')}
                  value={field.value ?? 0}
                  onChange={field.onChange}
                  currency={selectedCurrency}
                  scopeCurrencies={scopeCurrencies}
                  usdToBrl={usdToBrl}
                  helperText={errors.minOrderValue?.message}
                  dataCy='input-coupon-min-order-value'
                />
              )}
            />
          </SwitchField>

          <SwitchField
            id='switch-coupon-max-uses-per-buyer'
            label={t('createCoupon.enableMaxUsesPerBuyer')}
            checked={hasMaxUsesPerBuyer}
            onCheckedChange={(checked) =>
              setValue('hasMaxUsesPerBuyer', checked, { shouldValidate: true })
            }
          >
            <Input
              type='number'
              min={1}
              aria-label={t('createCoupon.maxUsesPerBuyer')}
              data-cy='input-coupon-max-uses-per-buyer'
              helperText={errors.maxUsesPerBuyer?.message}
              hasError={Boolean(errors.maxUsesPerBuyer)}
              {...register('maxUsesPerBuyer', {
                setValueAs: (value) => (value === '' ? 1 : Number(value)),
                validate: (value) =>
                  !hasMaxUsesPerBuyer ||
                  value >= 1 ||
                  t('createCoupon.maxUsesPerBuyerMin'),
              })}
            />
          </SwitchField>

          <SwitchField
            id='switch-coupon-max-uses'
            label={t('createCoupon.enableMaxUses')}
            checked={hasMaxUses}
            onCheckedChange={(checked) =>
              setValue('hasMaxUses', checked, { shouldValidate: true })
            }
          >
            <Input
              type='number'
              min={1}
              aria-label={t('createCoupon.maxUses')}
              subLabel={t('createCoupon.maxUsesHint')}
              placeholder={t('createCoupon.maxUsesPlaceholder')}
              data-cy='input-coupon-max-uses'
              helperText={errors.maxUses?.message}
              hasError={Boolean(errors.maxUses)}
              {...register('maxUses', {
                setValueAs: (value) => (value === '' ? null : Number(value)),
                validate: (value) =>
                  !hasMaxUses ||
                  (value !== null && value >= 1) ||
                  t('createCoupon.maxUsesMin'),
              })}
            />
          </SwitchField>

          <SectionTitle>{t('createCoupon.sectionValidity')}</SectionTitle>

          <div className='flex flex-col sm:flex-row gap-4'>
            <Input
              type='date'
              label={t('createCoupon.startsAt')}
              tooltip={t('createCoupon.startsAtTooltip')}
              data-cy='input-coupon-starts-at'
              helperText={errors.startsAt?.message}
              hasError={Boolean(errors.startsAt)}
              {...register('startsAt')}
            />

            <Input
              type='date'
              label={t('createCoupon.expiresAt')}
              tooltip={t('createCoupon.expiresAtTooltip')}
              data-cy='input-coupon-expires-at'
              helperText={errors.expiresAt?.message}
              hasError={Boolean(errors.expiresAt)}
              min={isEditing ? undefined : today}
              {...register('expiresAt', {
                validate: {
                  afterStartsAt: (value) =>
                    !value ||
                    !startsAt ||
                    value >= startsAt ||
                    t('createCoupon.expiresAtBeforeStartsAt'),
                  notInThePast: (value) =>
                    isEditing ||
                    !value ||
                    value >= today ||
                    t('createCoupon.expiresAtInThePast'),
                },
              })}
            />
          </div>
        </section>

        <div className='flex border-t items-center justify-end gap-4 px-6 py-4 w-full max-sm:pb-12'>
          <Button
            type='button'
            onClick={() => closeModal(isEditing ? 'editCoupon' : 'createCoupon')}
            variant='outline'
            data-cy='btn-cancel-create'
          >
            {t('createCoupon.cancel')}
          </Button>
          <Button type='submit' disabled={isSaving} data-cy='btn-save-coupon'>
            {isSaving
              ? t('createCoupon.saving')
              : isEditing
                ? t('createCoupon.saveChanges')
                : t('createCoupon.save')}
          </Button>
        </div>
      </form>
    </section>
  );
}
